// Deposco B2B orders beside A2000 (spec "Other sources: Deposco", docs/v7-next-steps.md "DEPOSCO"):
// the dataset `routing-deposco-orders` over A2000_Gold `dbo.vw_routing_deposco_orders`, one row per
// sales order (SO). Every Deposco mapping is in this file, so an answer from Isaac is a one-line
// change here: which column is which field, the status order, the DC and group rule, the facility's
// warehouse, the business units' names and the routing rule.
//
// An SO becomes one of the line's "pick tickets" (`SourceRow`, its number the SO #): the lines, the
// review statuses, carry-over, the baseline and every page then work as for A2000. A line is one
// customer + customer PO, or customer + PO + DC when the SO has a DC — only once it has a group too;
// until then it waits ("N Deposco orders waiting for a group").

import { formatShortDay } from "./days";
import type { SourceRow } from "./types";

/** Where a line comes from. A workbook row (History) is an A2000 one. */
export const LINE_SOURCES = ["A2000", "Deposco"] as const;
export type LineSource = (typeof LINE_SOURCES)[number];
/** The Source filter: every source, or one. */
export type SourceFilter = "all" | LineSource;
export const SOURCE_FILTERS: readonly { id: SourceFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "A2000", label: "A2000" },
  { id: "Deposco", label: "Deposco" },
];
export const isSourceFilter = (v: unknown): v is SourceFilter => SOURCE_FILTERS.some((s) => s.id === v);
/** Whether a line of `source` passes the Source filter. */
export const inSource = (source: LineSource, filter: SourceFilter) => filter === "all" || filter === source;

/** One Deposco SO, as the view gives it (no $ value: `routing-deposco-order-values`, managers only). Days are `YYYY-MM-DD`. */
export interface DeposcoRow {
  so_number: string;
  division_code: string | null;
  dlix: string | null;
  customer_order: string | null;
  co: string | null;
  customer_po: string | null;
  customer_code: string | null;
  customer_name: string | null;
  ship_to_name: string | null;
  dc_no: string | null;
  group_name: string | null;
  deposco_status: string | null;
  is_open: 0 | 1;
  facility: string | null;
  received_date: string | null;
  start_date: string | null;
  cancel_date: string | null;
  actual_ship_date: string | null;
  qty: number | null;
  allocated_qty: number | null;
  shipped_qty: number | null;
  line_count: number | null;
  ship_type: string | null;
  deposco_updated: string | null;
  source_loaded_at: string | null;
}

/** Which view column each of the app's fields reads. */
export const DEPOSCO_COLUMNS = {
  /** The SO / PT # column, and what a Deposco pre-route is typed with. */
  order: "so_number",
  po: "customer_po",
  customerCode: "customer_code",
  customerName: "customer_name",
  /** The division: the Deposco business unit. */
  division: "division_code",
  dc: "dc_no",
  group: "group_name",
  status: "deposco_status",
  facility: "facility",
  /** RECEIVED DATE. */
  received: "received_date",
  start: "start_date",
  cancel: "cancel_date",
  shippedOn: "actual_ship_date",
  qty: "qty",
  allocatedQty: "allocated_qty",
  shippedQty: "shipped_qty",
  dlix: "dlix",
  co: "co",
  customerOrder: "customer_order",
  shipTo: "ship_to_name",
} as const satisfies Record<string, keyof DeposcoRow>;
type Field = keyof typeof DEPOSCO_COLUMNS;

const text = (r: DeposcoRow, f: Field) => String(r[DEPOSCO_COLUMNS[f]] ?? "").trim();
const day = (r: DeposcoRow, f: Field) => {
  const v = r[DEPOSCO_COLUMNS[f]];
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null;
};
const amount = (r: DeposcoRow, f: Field) => {
  const v = r[DEPOSCO_COLUMNS[f]];
  return v === null || v === undefined || v === "" ? null : Number(v);
};

/**
 * Deposco's statuses, lowest first: a line shows its SOs' highest. Canceled is apart. Wave Built and
 * Staged (in the view on 2026-11-02) are placed by their names: to confirm with Isaac.
 */
export const DEPOSCO_STATUSES = [
  "New",
  "Released",
  "Back Ordered",
  "Wave Built",
  "Picking",
  "Pick Complete",
  "Partially Staged",
  "Staged",
  "Partially Shipped",
  "Ship Complete",
] as const;
export const DEPOSCO_CANCELED = "Canceled";
/** An SO with this status, or an actual ship date, has shipped. */
export const DEPOSCO_SHIPPED = "Ship Complete";
/** Allocated from this status on (or with allocated units): the Status column and Ask AI say so. */
export const DEPOSCO_ALLOCATED_FROM = "Released";

const norm = (s: string | null | undefined) =>
  String(s ?? "")
    .trim()
    .toLowerCase();
/** A status's place in `DEPOSCO_STATUSES`; -1 for Canceled and one this list does not know. */
export const deposcoRank = (status: string | null | undefined) =>
  DEPOSCO_STATUSES.findIndex((s) => s.toLowerCase() === norm(status));
export const isCanceledStatus = (status: string | null | undefined) => norm(status) === DEPOSCO_CANCELED.toLowerCase();

/** The Deposco facility's warehouse, as the warehouse picker names it. Unknown: the facility as is. */
export const DEPOSCO_FACILITY_WH: Readonly<Record<string, string>> = { PRS: "PE" };

/**
 * The business units' names: the division the pages show (an abbreviation, Admin → Divisions, under
 * corp `DEPOSCO_CORP`). Unknown: the code itself.
 */
export const DEPOSCO_DIVISIONS: Readonly<Record<string, string>> = { AERO: "AERO", EGC: "EGC", JA: "JA", WM: "WM" };
/** A Deposco division's corp, so its abbreviation never meets an A2000 one. */
export const DEPOSCO_CORP = "DEPOSCO";
export const deposcoDivisionName = (code: string) => DEPOSCO_DIVISIONS[code] ?? code;

/**
 * Whether an order with a DC needs a group before it is a line (Isaac). The line itself — customer +
 * PO (+ DC) — stands for "grouped as Deposco groups it" until the call with Isaac confirms it.
 */
export const DC_NEEDS_GROUP = true;
/**
 * Whether an SO is on a line only once allocated. On is the old rule (spec §2 step 2): an open SO
 * not allocated yet was no line, only a Deposco pre-route could route it. Off is Isaac's (10/30,
 * the app's rule from 11/02): Deposco is only the WMS, its ERP is M3, and an order reaches Deposco
 * only once M3 has allocated and picked it — so every open SO is a line, New ones too, and
 * Pre-route is A2000's (`PRE_ROUTE_ADD_SOURCES`). To confirm with Isaac.
 */
export const LINES_WAIT_FOR_ALLOCATION = false;

/** A Deposco line's grouping key: `DEP|<customer>|<PO>` or `DEP|<customer>|<PO>|<DC>`. */
export const DEPOSCO_PREFIX = "DEP|";
export const isDeposcoKey = (groupKey: string) => groupKey.startsWith(DEPOSCO_PREFIX);

const keyOf = (customer: string, po: string, dc: string) => `${DEPOSCO_PREFIX}${customer}|${po}${dc ? `|${dc}` : ""}`;

/** The line an SO is on, or null while it waits for a group (a DC, no group). */
export function deposcoLineKey(r: DeposcoRow): string | null {
  const dc = text(r, "dc");
  if (dc && DC_NEEDS_GROUP && !text(r, "group")) return null;
  return keyOf(text(r, "customerCode") || text(r, "customerName"), text(r, "po"), dc);
}

/** An open SO's line, from its source row (`deposcoSourceRow` keeps what `deposcoLineKey` reads); "" for A2000. */
export const deposcoKey = (s: SourceRow) =>
  s.deposco ? keyOf(s.customer_code || s.customer_name || "", s.customer_po ?? "", s.dc_no ?? "") : "";

/** Allocated: units allocated, or Released or later (never Canceled). A Deposco pre-route moves then. */
export const isAllocated = (r: DeposcoRow) =>
  !isCanceledStatus(r[DEPOSCO_COLUMNS.status]) &&
  ((amount(r, "allocatedQty") ?? 0) > 0 ||
    deposcoRank(r[DEPOSCO_COLUMNS.status]) >= deposcoRank(DEPOSCO_ALLOCATED_FROM));

/**
 * An SO the Worklist's lines hold (or will, once it has its group): open, not Canceled, and — while
 * `LINES_WAIT_FOR_ALLOCATION` is on — allocated.
 */
export const isLineSo = (r: DeposcoRow) =>
  r.is_open === 1 && !isCanceledStatus(r[DEPOSCO_COLUMNS.status]) && (!LINES_WAIT_FOR_ALLOCATION || isAllocated(r));

/** What a Deposco SO waits for before a pre-route of it moves, in words: "not on the Worklist yet". */
export const DEPOSCO_WAITS_FOR = LINES_WAIT_FOR_ALLOCATION ? "not allocated yet" : "not on the Worklist yet";

/** Shipped: Ship Complete, or an actual ship date. */
export const isShippedSo = (r: Pick<DeposcoInfo, "status" | "shippedOn">) =>
  norm(r.status) === DEPOSCO_SHIPPED.toLowerCase() || !!r.shippedOn;

/**
 * What a Deposco SO adds to its `SourceRow`: only the columns A2000 has no place for — its SO #,
 * DC, ship-to and line are the row's own (`pick_ticket`, `dc_no`, `store`, `deposcoKey`): the
 * Worklist sends every open SO.
 */
export interface DeposcoInfo {
  dlix: string;
  co: string;
  customerOrder: string;
  group: string;
  status: string;
  shippedOn: string | null;
}

/**
 * An SO as a line's pick ticket: the `SourceRow` the rules read, with its Deposco columns. Deposco
 * has no CUST_REF1/2: its routing rule is `routingRuleFor`'s (`routing-rules.ts`), never the row's.
 */
export function deposcoSourceRow(r: DeposcoRow): SourceRow {
  const facility = text(r, "facility");
  const division = text(r, "division");
  return {
    pick_ticket: text(r, "order"),
    wh: DEPOSCO_FACILITY_WH[facility] ?? (facility || null),
    ctrl_no: null,
    corp: DEPOSCO_CORP,
    customer_code: text(r, "customerCode") || null,
    customer_name: text(r, "customerName") || text(r, "customerCode") || null,
    div: division || null,
    division: division ? deposcoDivisionName(division) : null,
    store: text(r, "shipTo") || null,
    customer_po: text(r, "po") || null,
    pt_date: day(r, "received"),
    start_date: day(r, "start"),
    cancel_date: day(r, "cancel"),
    qty: amount(r, "qty"),
    wms_shipment: null,
    in_wms: 1,
    routing_open_bdays: null,
    routing_due_bdays: null,
    // The payload says when Deposco was loaded (`deposcoLoadedAt`): not on each SO.
    source_loaded_at: null,
    dc_no: text(r, "dc") || null,
    dc_name: null,
    deposco: deposcoInfo(r),
  };
}

export function deposcoInfo(r: DeposcoRow): DeposcoInfo {
  return {
    dlix: text(r, "dlix"),
    co: text(r, "co"),
    customerOrder: text(r, "customerOrder"),
    group: text(r, "group"),
    status: text(r, "status"),
    shippedOn: day(r, "shippedOn"),
  };
}

/**
 * The open SOs as the Worklist's source rows, beside A2000's — every one, New too
 * (`LINES_WAIT_FOR_ALLOCATION` off) — and those that wait for a group (a DC, no group): not lines yet.
 */
export function deposcoSource(rows: readonly DeposcoRow[]): { source: SourceRow[]; waiting: DeposcoRow[] } {
  const source: SourceRow[] = [];
  const waiting: DeposcoRow[] = [];
  for (const r of rows) {
    if (!text(r, "order") || !isLineSo(r)) continue;
    if (deposcoLineKey(r) === null) waiting.push(r);
    else source.push(deposcoSourceRow(r));
  }
  return { source, waiting };
}

/** An SO # as typed: no spaces, upper case ("so230792" is SO230792). */
export const soNoOf = (typed: string) => typed.replace(/\s+/g, "").toUpperCase();
/** An SO #'s shape: letters then digits (SO230792). A pick ticket is digits only. */
export const isSoNo = (v: string) => /^[A-Z]{1,4}\d{1,12}$/.test(v);

/** A line's SOs by status: its highest (Canceled apart), and whether they differ (MIXED). */
export interface DeposcoStatus {
  /** The highest of the SOs not canceled; "Canceled" when every one is. */
  status: string;
  /** Different statuses among the SOs not canceled. */
  mixed: boolean;
  canceled: number;
  /** Each SO and its status, lowest status first: the one behind first. */
  sos: { so: string; status: string }[];
}

export function deposcoStatus(sos: readonly { so: string; status: string }[]): DeposcoStatus | null {
  if (!sos.length) return null;
  const live = sos.filter((s) => !isCanceledStatus(s.status));
  const canceled = sos.length - live.length;
  const top = [...live].sort((a, b) => deposcoRank(b.status) - deposcoRank(a.status))[0];
  return {
    status: top ? top.status || "—" : DEPOSCO_CANCELED,
    mixed: new Set(live.map((s) => norm(s.status))).size > 1,
    canceled,
    sos: [...sos].sort(
      (a, b) =>
        Number(isCanceledStatus(a.status)) - Number(isCanceledStatus(b.status)) ||
        deposcoRank(a.status) - deposcoRank(b.status) ||
        a.so.localeCompare(b.so),
    ),
  };
}

/** The Deposco status cell and its filter value: "Partially Staged · MIXED", "Released · 1 canceled". */
export const deposcoStatusText = (d: DeposcoStatus) =>
  [d.status, d.mixed ? "MIXED" : "", d.canceled && d.status !== DEPOSCO_CANCELED ? `${d.canceled} canceled` : ""]
    .filter(Boolean)
    .join(" · ");

/** The flag: the line's SOs Deposco shipped (Ship Complete or a ship date), of how many. */
export interface DeposcoShipped {
  shipped: { so: string; status: string; on: string | null }[];
  of: number;
  all: boolean;
  latest: string | null;
}

export function deposcoShipped(
  sos: readonly (Pick<DeposcoInfo, "status" | "shippedOn"> & { so: string })[],
): DeposcoShipped | null {
  const shipped = sos
    .filter(isShippedSo)
    .map((s) => ({ so: s.so, status: s.status, on: s.shippedOn }))
    .sort((a, b) => a.so.localeCompare(b.so));
  if (!shipped.length) return null;
  const latest =
    shipped
      .map((s) => s.on)
      .filter((d): d is string => !!d)
      .sort()
      .at(-1) ?? null;
  return { shipped, of: sos.length, all: shipped.length === sos.length, latest };
}

/** The chip: "Shipped in Deposco · 10/19", or "2 of 5 SOs shipped in Deposco". */
export function deposcoShippedText(d: DeposcoShipped): string {
  if (!d.all) return `${d.shipped.length} of ${d.of} SOs shipped in Deposco`;
  return d.latest ? `Shipped in Deposco · ${formatShortDay(d.latest)}` : "Shipped in Deposco · Ship Complete";
}

/** Its tooltip, one line per SO: "SO230792 · Ship Complete · 10/19". */
export const deposcoShippedLines = (d: DeposcoShipped) =>
  d.shipped.map((s) => [s.so, s.status || "shipped", s.on ? formatShortDay(s.on) : ""].filter(Boolean).join(" · "));

/** How a gone SO left Deposco's open orders: Canceled, Shipped, or gone (past the view's 30 days). */
export function goneWhy(closed: Pick<DeposcoRow, "deposco_status" | "actual_ship_date"> | undefined): string {
  if (!closed) return "no longer in Deposco";
  if (isCanceledStatus(closed.deposco_status)) return "Canceled in Deposco";
  if (norm(closed.deposco_status) === DEPOSCO_SHIPPED.toLowerCase() || closed.actual_ship_date)
    return `Shipped in Deposco${closed.actual_ship_date ? ` ${formatShortDay(closed.actual_ship_date.slice(0, 10))}` : ""}`;
  return `${closed.deposco_status || "Closed"} in Deposco`;
}

/** The flag as a filter value, as the A2000 one. */
export const DEPOSCO_SHIPPED_VALUES = ["All", "Some", "No"] as const;
export const deposcoShippedValue = (d: DeposcoShipped | null): (typeof DEPOSCO_SHIPPED_VALUES)[number] =>
  d ? (d.all ? "All" : "Some") : "No";

/** An open SO that is no line yet, as the Worklist lists it: only the columns its list shows. */
export type DeposcoBrief = Pick<
  DeposcoRow,
  | "so_number"
  | "customer_code"
  | "customer_name"
  | "customer_po"
  | "dc_no"
  | "deposco_status"
  | "received_date"
  | "cancel_date"
  | "qty"
>;

/** Each SO's brief: the columns its list shows, never the whole row. */
export const deposcoBriefs = (rows: readonly DeposcoRow[]): DeposcoBrief[] =>
  rows.map((r) => ({
    so_number: r.so_number,
    customer_code: r.customer_code,
    customer_name: r.customer_name,
    customer_po: r.customer_po,
    dc_no: r.dc_no,
    deposco_status: r.deposco_status,
    received_date: r.received_date,
    cancel_date: r.cancel_date,
    qty: r.qty,
  }));
